import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  UserSearchResponse,
  UserSearchResult,
} from '@shared/types';
import { ADMIN_USER_WINDOWS, type AdminUserWindow } from '@shared/constants';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import { listUsers, type ListUsersParams } from '../../services/users';
import { ApiError } from '../../services/api';
import { BannedPill, OnlinePill } from './pills';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Admin users list. Server-side search (`q`) + pagination. The search input
 * is debounced 300ms to avoid hammering the API on each keystroke. Changing
 * `q` resets the current page to 1 — otherwise an admin typing a new query
 * on page 7 would get a stale empty page.
 *
 * The `online` / `banned` / `bots` / `since` / `active` filters live in the URL, not in
 * component state, because they are the landing target for the dashboard's
 * metric cards: "17 online" links here as `?online=true`. Keeping them in the
 * URL is what makes that link work, and makes the filtered view shareable and
 * survivable across a refresh. Every filter is applied SERVER-side — filtering
 * a page after it was fetched would return short pages and a total that
 * disagrees with the rows.
 */

/** Filters that arrive from the dashboard as query params. */
interface UrlFilters {
  online: boolean;
  banned: boolean;
  /** Tri-state: true = bots only, false = exclude bots, undefined = both. */
  bots: boolean | undefined;
  /** Signup window, on `createdAt`. */
  since: AdminUserWindow | undefined;
  /** Activity window, on `lastActiveAt` — "online now" is `online`. */
  active: AdminUserWindow | undefined;
}

const WINDOW_LABELS: Record<AdminUserWindow, string> = {
  '24h': 'Last 24 hours',
  '3d': 'Last 3 days',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

/** A junk value (`?since=lastyear`) is dropped, never forwarded. */
function readWindow(value: string | null): AdminUserWindow | undefined {
  return (ADMIN_USER_WINDOWS as readonly string[]).includes(value ?? '')
    ? (value as AdminUserWindow)
    : undefined;
}

function readFilters(params: URLSearchParams): UrlFilters {
  const bots = params.get('bots');
  return {
    online: params.get('online') === 'true',
    banned: params.get('banned') === 'true',
    // Dashboard links carry `bots=false` on every player-facing card, so the
    // list matches the number that was clicked.
    bots: bots === 'true' ? true : bots === 'false' ? false : undefined,
    since: readWindow(params.get('since')),
    active: readWindow(params.get('active')),
  };
}
export default function UsersList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Keyed on the SERIALIZED string, not the URLSearchParams instance: the
  // instance is a fresh object on every render, so depending on it would
  // rebuild `filters` each time and re-fire the fetch effect in a loop.
  const searchKey = searchParams.toString();
  const filters = useMemo(
    () => readFilters(new URLSearchParams(searchKey)),
    [searchKey],
  );
  const activeFilterCount =
    Number(filters.online) +
    Number(filters.banned) +
    Number(filters.bots !== undefined) +
    Number(filters.since !== undefined) +
    Number(filters.active !== undefined);

  const setFilter = useCallback(
    (key: keyof UrlFilters, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value === null) next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const [page, setPage] = useState(1);

  // Seeded from `?q=` so the search box is deep-linkable — the user detail
  // page's Devices table links here with a device id to answer "which other
  // accounts use this device?" (#device-tracking). Read LAZILY, once at
  // mount: keeping the box subscribed to the URL would fight the debounced
  // local state on every keystroke. Consequence to know: this is MOUNT-only,
  // so navigating /users?q=A -> /users?q=B without unmounting keeps showing A.
  // Unreachable from the Devices link (Detail -> List remounts).
  const readInitialQuery = () => new URLSearchParams(searchKey).get('q')?.trim() ?? '';
  const [searchRaw, setSearchRaw] = useState(readInitialQuery);
  const [search, setSearch] = useState(readInitialQuery);

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: UserSearchResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchRaw.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchRaw]);

  // Reset to page 1 when the debounced query OR any filter changes — a filter
  // applied on page 7 would otherwise land on a page past the new result set.
  useEffect(() => {
    setPage(1);
  }, [search, filters]);

  const reqCounterRef = useRef(0);
  const fetchUsers = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const params: ListUsersParams = {
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        online: filters.online || undefined,
        banned: filters.banned || undefined,
        bots: filters.bots,
        since: filters.since,
        active: filters.active,
      };
      const data = await listUsers(params);
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load users';
      setState({ status: 'error', message });
    }
  }, [page, search, filters]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const rows = useMemo<UserSearchResult[]>(() => {
    return state.status === 'ready' ? state.data.data : [];
  }, [state]);

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    if (state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.limit));
  }, [state]);

  const columns: DataTableColumn<UserSearchResult>[] = useMemo(
    () => [
      {
        key: 'displayName',
        header: 'Display name',
        render: (u) => (
          <span className="font-medium text-slate-900">{u.displayName}</span>
        ),
      },
      {
        key: 'username',
        header: 'Username',
        render: (u) => <span className="text-slate-700">{u.username}</span>,
      },
      {
        key: 'email',
        header: 'Email',
        render: (u) => (
          <span className="text-slate-700">{u.email ?? '—'}</span>
        ),
      },
      {
        key: 'authProvider',
        header: 'Provider',
        render: (u) => (
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase tracking-wide">
            {u.authProvider}
          </span>
        ),
      },
      {
        key: 'language',
        header: 'Lang',
        render: (u) => (
          <span className="font-mono text-xs text-slate-700 uppercase">
            {u.language}
          </span>
        ),
      },
      {
        key: 'presence',
        header: 'Presence',
        render: (u) => <OnlinePill isOnline={u.isOnline} />,
      },
      {
        key: 'status',
        header: 'Status',
        render: (u) => (
          <div className="flex flex-wrap items-center gap-1">
            <BannedPill isBanned={u.isBanned} />
            {u.isBot && (
              <span
                className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 uppercase tracking-wide"
                data-testid={`users-row-bot-${u.id}`}
              >
                Bot
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'matches',
        header: 'Matches · Wins',
        render: (u) => (
          <span className="font-mono text-sm text-slate-700">
            {u.totalMatches} · {u.totalWins}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        render: (u) => (
          <span className="text-xs text-slate-500">
            {formatDate(u.createdAt)}
          </span>
        ),
      },
      {
        key: 'lastActiveAt',
        header: 'Last active',
        render: (u) => (
          <span className="text-xs text-slate-500">
            {formatDate(u.lastActiveAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4" data-testid="users-list-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Users</h1>
      </div>

      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600 flex-1 min-w-[200px]">
          Search
          <input
            type="text"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            placeholder="Email or display name…"
            data-testid="users-search-input"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>

        <FilterToggle
          label="Online now"
          checked={filters.online}
          onChange={(on) => setFilter('online', on ? 'true' : null)}
          testId="users-filter-online"
        />
        <FilterToggle
          label="Banned"
          checked={filters.banned}
          onChange={(on) => setFilter('banned', on ? 'true' : null)}
          testId="users-filter-banned"
        />
        <label className="flex flex-col text-xs text-slate-600">
          Bots
          <select
            value={filters.bots === undefined ? '' : String(filters.bots)}
            onChange={(e) => setFilter('bots', e.target.value || null)}
            data-testid="users-filter-bots"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">Include bots</option>
            <option value="false">Exclude bots</option>
            <option value="true">Bots only</option>
          </select>
        </label>

        <WindowSelect
          label="Signed up"
          value={filters.since}
          onChange={(v) => setFilter('since', v)}
          testId="users-filter-since"
        />
        <WindowSelect
          label="Last active"
          value={filters.active}
          onChange={(v) => setFilter('active', v)}
          testId="users-filter-active"
        />

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="users-filters-clear"
            className="text-xs rounded border border-slate-300 bg-white px-2 py-1 text-slate-700 hover:bg-slate-50"
          >
            Clear filters ({activeFilterCount})
          </button>
        )}
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="users-list-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <DataTable<UserSearchResult>
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        loading={state.status === 'loading'}
        onRowClick={(u) => navigate(`/users/${u.id}`)}
        empty={
          search
            ? 'No users match that search.'
            : activeFilterCount > 0
              ? 'No users match these filters.'
              : 'No users yet.'
        }
        testId="users-table"
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={state.status === 'loading'}
      />
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

/** "Any time" + one option per `ADMIN_USER_WINDOWS` entry. */
function WindowSelect({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: AdminUserWindow | undefined;
  onChange: (value: string | null) => void;
  testId: string;
}) {
  return (
    <label className="flex flex-col text-xs text-slate-600">
      {label}
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testId}
        className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
      >
        <option value="">Any time</option>
        {ADMIN_USER_WINDOWS.map((w) => (
          <option key={w} value={w}>
            {WINDOW_LABELS[w]}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Checkbox styled as a compact filter toggle for the users filter bar. */
function FilterToggle({
  label,
  checked,
  onChange,
  testId,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-slate-700 pb-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />
      {label}
    </label>
  );
}
